import { Anchor } from '@components/atoms/Anchor';
import LinkButton from '@components/atoms/buttons/LinkButton';
import OutlineButton from '@components/atoms/buttons/OutlineButton';
import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import GridBox from '@components/atoms/GridBox';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import { Heading1, Heading2, Paragraph } from '@components/atoms/Typography';
import PageContainer from '@components/molecules/PageContainer';
import BREAKPOINTS from '@lib/constants/breakpoints';
import env from '@lib/constants/env';
import ROUTES from '@lib/constants/routes';
import airtableBases, { ClubData } from '@lib/helpers/airtableBases';
import { openHelpDeskChat } from '@lib/helpers/chat';
import { validateEmail } from '@lib/helpers/misc';
import { useShareModal } from '@lib/hooks/modal';
import { STATES } from '@shared/constants';
import { GetStaticProps } from 'next';
import { BreadcrumbJsonLd } from 'next-seo';
import Link from 'next/link';
import styled from 'styled-components';

const Header = styled.header`
  position: relative;
  padding-top: 80px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 50px;
  }
`;

const Wrapper = styled.div`
  padding: 32px 64px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding: 32px;
  }
`;

const List = styled.ul`
  margin: 32px auto;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr;
  max-width: 600px;
`;

interface Props {
  club: ClubData;
  uf: string;
}

const MotoClubPage = ({ club, uf }: Props) => {
  if (!uf) return <PageContainer loading />;

  const stateName = STATES[uf.toUpperCase() as keyof typeof STATES];

  // eslint-disable-next-line react/no-unused-prop-types
  function renderValue({ key, value }: { key: string; value: string }) {
    if (key.match(/estado/i)) {
      return (
        <Link href={ROUTES.stateClub(uf)} passHref>
          <Anchor>{stateName}</Anchor>
        </Link>
      );
    }

    if (validateEmail(value)) {
      return (
        <Anchor href={`mailto:${value}`} target="_blank">
          {value}
        </Anchor>
      );
    }

    if (!value) return 'Não informado';
    return value;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [showShare] = useShareModal({
    shareInfo: {
      text: club.Motoclube,
      url: `${env.baseUrl}${ROUTES.club(uf, club.Motoclube)}`,
    },
  });

  return (
    <PageContainer
      title={`${club.Motoclube} - Moto Clube`}
      metaDescription={`Confira as informações do moto grupo ${
        club.Motoclube
      } da cidade de ${club.Cidade || ''} no estado de ${stateName}`}
      customAmpPage
    >
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Luna',
            item: env.baseUrl,
          },
          {
            position: 2,
            name: 'Moto Clubes',
            item: `${env.baseUrl}${ROUTES.clubs}`,
          },
          {
            position: 3,
            name: `Moto clubes em ${stateName}`,
            item: `${env.baseUrl}${ROUTES.stateClub(uf)}`,
          },
          {
            position: 4,
            name: club.Motoclube,
            item: `${env.baseUrl}${ROUTES.club(uf, club.Motoclube)}`,
          },
        ]}
      />
      <Header>
        <Wrapper>
          <Heading1 center>{club.Motoclube}</Heading1>
          <List>
            {Object.entries(club)
              .filter(([key]) => !key.match('slug'))
              .map(([key, value]) => (
                <li key={key}>
                  <strong>{key}:</strong>{' '}
                  {renderValue({ key, value: value.toString().trim() })}
                </li>
              ))}
          </List>
          <GridBox justifyContent="center">
            <OutlineButton onClick={showShare}>Compartilhar</OutlineButton>
          </GridBox>
          <LinkButton
            style={{ marginTop: 32 }}
            onClick={async () => {
              openHelpDeskChat();
            }}
          >
            Quero editar as informações
          </LinkButton>
        </Wrapper>
      </Header>
      <Section pastel>
        <Heading2 center>Quer adicionar seu moto clube?</Heading2>
        <Spacing top={2}>
          <Paragraph center>
            Caso participe de um moto clube, você pode adicionar seu grupo para
            que outros motociclistas o encontrem!
          </Paragraph>
        </Spacing>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://airtable.com/shr4RSpZLUYF8Z0oW"
        >
          <PrimaryButton pink style={{ margin: '32px auto 0' }}>
            Adicionar Clube
          </PrimaryButton>
        </a>
      </Section>
      <Section>
        <Heading2 center>Quer cotar um seguro em menos de 1 minuto?</Heading2>
        <Spacing top={2}>
          <Paragraph center>
            Aqui na Luna você consegue fazer cotação para o seguro de moto em
            tempo recorde!
          </Paragraph>
        </Spacing>
        <Link passHref href={ROUTES.startForm()}>
          <a>
            <PrimaryButton style={{ margin: '32px auto 0' }}>
              Cotar Agora
            </PrimaryButton>
          </a>
        </Link>
      </Section>
    </PageContainer>
  );
};

export async function getStaticPaths() {
  return {
    paths: ['/moto-clubes/ba/amantes-do-asfalto'],
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const uf = params?.uf?.toString().toLowerCase().trim() as string;
  const slug = params?.slug
    ?.toString()
    .toLowerCase()
    .trim()
    .replace(/"/g, '') as string;

  const club = await airtableBases.getMotoClubBySlugAndState({
    slug,
    uf,
  });

  if (!club) return { notFound: true };

  return {
    props: { club, uf },
    revalidate: 3600,
  };
};

export default MotoClubPage;
